import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Portrait, initials, split, useCopy } from "./HelpersUI";

// Sixth leadership card: the founder.
export function FounderCard() {
  const { c } = useCopy();
  const [name, role, bio] = split(c.board_6, 3);
  return (
    <article data-testid="helpers-board-6" className="h-card rounded-[1.75rem] bg-white p-7">
      <Portrait slot="helpers_16_founder" name={name} fallback={initials(name)} />
      <h3 className="h-display text-lg font-bold text-[color:var(--h-ink)]">{name}</h3>
      <p className="text-sm font-medium text-[color:var(--h-mint-deep)]">{role}</p>
      <p className="mt-3 text-sm leading-relaxed text-slate-600">{bio}</p>
    </article>
  );
}

const STEPS = ["Order received", "Calibrated", "Out for delivery", "Delivered"];

// Order tracking that tracks nothing: validated in the browser, never stored or sent.
export function TrackPage() {
  const { c } = useCopy();
  const [num, setNum] = useState("");
  const [err, setErr] = useState("");
  const [step, setStep] = useState(-1);
  const timers = useRef([]);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  const submit = (e) => {
    e.preventDefault();
    if (!/^[A-Za-z0-9-]{4,24}$/.test(num.trim())) { setErr("Please enter a valid order number (4–24 letters or numbers)."); return; }
    setErr("");
    setNum("");
    timers.current.forEach(clearTimeout);
    setStep(0);
    timers.current = [1, 2, 3].map((s) => setTimeout(() => setStep(s), s * 900));
  };
  const done = step === 3;
  return (
    <section data-testid="helpers-track" className="mx-auto max-w-2xl px-5 py-14 sm:px-8 lg:py-20">
      <h1 className="h-display text-4xl font-extrabold tracking-tight text-[color:var(--h-ink)] sm:text-5xl">{c.track_title}</h1>
      <p className="mt-4 text-base leading-relaxed text-slate-600">{c.track_body}</p>
      <form onSubmit={submit} className="mt-8 flex flex-col gap-3 sm:flex-row" data-testid="helpers-track-form" noValidate>
        <input value={num} onChange={(e) => setNum(e.target.value)} placeholder="e.g. 333-104729" autoComplete="off" aria-label="Order number"
          data-testid="helpers-track-input" maxLength={24}
          className="flex-1 rounded-full border border-[color:var(--h-line)] bg-white px-6 py-4 text-base outline-none focus:shadow-[0_0_0_4px_rgba(31,138,100,.15)]" />
        <button type="submit" data-testid="helpers-track-submit" className="h-btn rounded-full bg-[color:var(--h-mint-deep)] px-8 py-4 font-semibold text-white hover:bg-[color:var(--h-mint-ink)]">{c.track_button}</button>
      </form>
      {err && <p data-testid="helpers-track-error" className="mt-3 text-sm text-rose-600">{err}</p>}
      {step >= 0 && (
        <div data-testid="helpers-track-result" className="h-card mt-10 rounded-[2rem] bg-white p-7">
          <div className="h-3 overflow-hidden rounded-full bg-[color:var(--h-mint-soft)]">
            <div data-testid="helpers-track-bar" className="h-full rounded-full bg-gradient-to-r from-[#9fe3c4] via-[#ffd8c2] to-[#1f8a64] transition-[width] duration-700 ease-out" style={{ width: `${((step + 1) / 4) * 100}%` }} />
          </div>
          <ol className="mt-4 grid grid-cols-4 gap-2 text-[11px] font-medium text-slate-500 sm:text-xs">
            {STEPS.map((s, i) => <li key={s} className={i <= step ? "text-[color:var(--h-mint-deep)]" : ""}>{s}</li>)}
          </ol>
          {done && (
            <div className="h-rise mt-6">
              <p data-testid="helpers-track-done" className="h-display text-2xl font-bold text-[color:var(--h-ink)]">{c.track_done}</p>
              <p data-testid="helpers-track-date" className="mt-1 text-sm text-slate-500">Delivered {c.track_date}</p>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// Careers form with one position. Breaks 3 s after opening (or on submit), then closes. Nothing is kept or sent.
export function CareersModal({ open, onClose }) {
  const { c } = useCopy();
  const [glitch, setGlitch] = useState(false);
  const timers = useRef([]);
  const breakNow = () => {
    if (glitch) return;
    setGlitch(true);
    timers.current.push(setTimeout(() => { setGlitch(false); onClose(); }, 700));
  };
  useEffect(() => {
    if (!open) return undefined;
    setGlitch(false);
    timers.current = [setTimeout(breakNow, 3000)];
    return () => { timers.current.forEach(clearTimeout); setGlitch(false); };
  }, [open]);  // eslint-disable-line react-hooks/exhaustive-deps
  const field = "w-full rounded-2xl border border-[color:var(--h-line)] bg-[color:var(--h-bg)] px-5 py-3.5 text-base outline-none";
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="helpers-careers-modal" className={`h-site w-[calc(100%-2rem)] max-w-md rounded-[2rem] border-0 bg-white p-8 shadow-2xl sm:rounded-[2rem] [&>button:last-child]:hidden ${glitch ? "h-glitch-box" : ""}`}>
        <button type="button" onClick={onClose} data-testid="helpers-careers-close" aria-label="Close"
          className="absolute right-5 top-5 rounded-full p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"><X size={18} /></button>
        <form onSubmit={(e) => { e.preventDefault(); breakNow(); }} autoComplete="off" data-testid="helpers-careers-form" className="space-y-3">
          <DialogTitle className="h-display text-2xl font-bold text-[color:var(--h-ink)]">{c.careers_form_title}</DialogTitle>
          <DialogDescription className="text-[15px] leading-relaxed text-slate-600">{c.careers_form_body}</DialogDescription>
          <input aria-label="Full name" placeholder="Full name" data-testid="helpers-careers-name" className={field} />
          <input aria-label="Email" placeholder="Email" type="email" data-testid="helpers-careers-email" className={field} />
          <select aria-label="Position" defaultValue="" data-testid="helpers-careers-position" className={field}>
            <option value="" disabled>Choose a position</option>
            <option value="subject">{c.careers_position}</option>
          </select>
          <button type="submit" data-testid="helpers-careers-submit" className="h-btn w-full rounded-full bg-[color:var(--h-mint-deep)] py-4 font-semibold text-white hover:bg-[color:var(--h-mint-ink)]">{c.careers_submit}</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
